export type EngVelocityView = "velocity" | "ai-engineering";

export type AiEngineeringMetricState = "measured" | "insufficient_data" | "unavailable";

export type AiEngineeringMetric = {
  denominator: number;
  numerator: number;
  state: AiEngineeringMetricState;
  value: number | null;
};

export type AiEngineeringEvaluations = {
  availability: "live" | "unavailable";
  calibrated: boolean;
  completedRuns: number;
  coverage: AiEngineeringMetric;
  eligibleRuns: number;
  failedRuns: number;
  judgeModels: string[];
  labels: { fail: number; partial: number; pass: number; unknown: number };
  passRate: AiEngineeringMetric;
  rubricVersions: string[];
  sampledRuns: number;
  scoredRuns: number;
};

export type AiEngineeringStandardRow = {
  fail: number;
  label: string;
  pass: number;
  passRate: number | null;
  unknown: number;
};

export type AiEngineeringSkillRow = {
  adherenceRate: number | null;
  completed: number;
  invocations: number;
  skill: string;
};

export type AiEngineeringBreakoutRow = {
  aiComments: number;
  authoredPrsOpened: number | null;
  cohort: string;
  kind: "all" | "engineer" | "repo";
  prCount: number;
  sessionCount: number;
  skillInvocations: number;
  standardsPassRate: number | null;
  turnCount: number;
};

export type AiEngineeringReviewDimension = {
  dimension: string;
  findings: number;
  state: AiEngineeringMetricState;
};

export type AiEngineeringReviewWorkflow = {
  availability: "live" | "partial" | "unavailable";
  dimensions: AiEngineeringReviewDimension[];
  findingCoverage: AiEngineeringMetric;
  findingsActedOn: AiEngineeringMetric;
  reviewCoverage: AiEngineeringMetric;
};

export type AiEngineeringOutcomeCohort = {
  changesRequestedShare: AiEngineeringMetric;
  cohort: "ai" | "other" | "all";
  medianHoursToMerge: number | null;
  medianHumanComments: number | null;
  medianHumanReviews: number | null;
  medianLinesChanged: number | null;
  mergedPrs: number;
  revertRate: AiEngineeringMetric;
  reworkShare: AiEngineeringMetric;
};

export type AiEngineeringOutcomes = {
  /** Merged PRs with AI authoring provenance divided by merged PRs. */
  adoption: AiEngineeringMetric;
  aiReview: {
    /** PRs with a tagged AI finding that received a commit after the first finding. */
    changedAfterFinding: AiEngineeringMetric;
    changesRequestedOnOtherPrs: AiEngineeringMetric;
    changesRequestedOnReviewedPrs: AiEngineeringMetric;
    medianHumanCommentsOnOtherPrs: number | null;
    medianHumanCommentsOnReviewedPrs: number | null;
    mergedPrsReviewed: number;
    mergedPrsWithFindings: number;
    /** Optional during control-plane/web rolling deploys. */
    workflow?: AiEngineeringReviewWorkflow;
  };
  availability: "live" | "unavailable";
  cohorts: AiEngineeringOutcomeCohort[];
  costPerAiMergedPr: number | null;
  costPerDeployedTicket: number | null;
  deployedTickets: number | null;
  mergedPrs: number;
  mergedPrsWithAi: number;
  revertedPrs: number;
  /** User sessions in the window linked from a PR body divided by user sessions. */
  sessionConversion: AiEngineeringMetric;
};

export type AiEngineeringTrendPoint = {
  adoption: AiEngineeringMetric;
  costPerAiMergedPr: AiEngineeringMetric;
  date: string;
  efficiency: AiEngineeringMetric;
  findingsActedOn: AiEngineeringMetric;
  reviewCoverage: AiEngineeringMetric;
  reviewRequests: number;
  sessionConversion: AiEngineeringMetric;
  standardsCompliance: AiEngineeringMetric;
  taskCompletion: AiEngineeringMetric;
};

export type AiEngineeringSnapshotResponse = {
  breakouts: AiEngineeringBreakoutRow[];
  evaluations: AiEngineeringEvaluations;
  freshness: {
    github: "live" | "partial" | "unavailable";
    operators: "live" | "unavailable";
  };
  generatedAt: string;
  outcomes: AiEngineeringOutcomes;
  provenance: {
    models: string[];
    prCount: number;
    sessionCount: number;
  };
  review: {
    aiComments: number;
    completedTurns: number;
    dimensions: AiEngineeringReviewDimension[];
    humanComments: number;
    prCount: number;
    reviewRequests: number;
    reviewSessions: number;
    successfulTurns: number;
  };
  skills: AiEngineeringSkillRow[];
  standards: AiEngineeringStandardRow[];
  summary: {
    codeQuality: AiEngineeringMetric;
    efficiency: AiEngineeringMetric;
    reviewCoverage: AiEngineeringMetric;
    skillAdherence: AiEngineeringMetric;
    standardsCompliance: AiEngineeringMetric;
    taskCompletion: AiEngineeringMetric;
  };
  status: "live" | "partial" | "unavailable";
  trends: AiEngineeringTrendPoint[];
  usage: {
    costUsd: number | null;
    outputTokens: number;
    totalTokens: number;
  };
  window: {
    label: string;
    since: string;
    until: string;
  };
};

export function isEngVelocityView(value: string): value is EngVelocityView {
  return value === "velocity" || value === "ai-engineering";
}

export function metricStateLabel(state: AiEngineeringMetricState): string {
  if (state === "measured") return "Measured";
  if (state === "insufficient_data") return "Insufficient data";
  return "Unavailable";
}

export function createUnavailableAiEngineeringOutcomes(): AiEngineeringOutcomes {
  const unavailable: AiEngineeringMetric = {
    denominator: 0,
    numerator: 0,
    state: "unavailable",
    value: null,
  };
  const cohort = (name: AiEngineeringOutcomeCohort["cohort"]): AiEngineeringOutcomeCohort => ({
    changesRequestedShare: unavailable,
    cohort: name,
    medianHoursToMerge: null,
    medianHumanComments: null,
    medianHumanReviews: null,
    medianLinesChanged: null,
    mergedPrs: 0,
    revertRate: unavailable,
    reworkShare: unavailable,
  });
  return {
    adoption: unavailable,
    aiReview: {
      changedAfterFinding: unavailable,
      changesRequestedOnOtherPrs: unavailable,
      changesRequestedOnReviewedPrs: unavailable,
      medianHumanCommentsOnOtherPrs: null,
      medianHumanCommentsOnReviewedPrs: null,
      mergedPrsReviewed: 0,
      mergedPrsWithFindings: 0,
      workflow: {
        availability: "unavailable",
        dimensions: [],
        findingCoverage: unavailable,
        findingsActedOn: unavailable,
        reviewCoverage: unavailable,
      },
    },
    availability: "unavailable",
    cohorts: [cohort("ai"), cohort("other"), cohort("all")],
    costPerAiMergedPr: null,
    costPerDeployedTicket: null,
    deployedTickets: null,
    mergedPrs: 0,
    mergedPrsWithAi: 0,
    revertedPrs: 0,
    sessionConversion: unavailable,
  };
}

export function createUnavailableAiEngineeringSnapshot(): AiEngineeringSnapshotResponse {
  const unavailableMetric: AiEngineeringMetric = {
    denominator: 0,
    numerator: 0,
    state: "unavailable",
    value: null,
  };
  return {
    breakouts: [],
    evaluations: createUnavailableAiEngineeringEvaluations(),
    freshness: { github: "unavailable", operators: "unavailable" },
    generatedAt: new Date(0).toISOString(),
    outcomes: createUnavailableAiEngineeringOutcomes(),
    provenance: { models: [], prCount: 0, sessionCount: 0 },
    review: {
      aiComments: 0,
      completedTurns: 0,
      dimensions: [
        "Requirements",
        "Correctness",
        "Tests",
        "Security & privacy",
        "Maintainability",
        "Scope",
      ].map((dimension) => ({ dimension, findings: 0, state: "unavailable" })),
      humanComments: 0,
      prCount: 0,
      reviewRequests: 0,
      reviewSessions: 0,
      successfulTurns: 0,
    },
    skills: [],
    standards: ["Formatting", "Lint", "Typecheck", "Tests", "Build", "Security & privacy"].map(
      (label) => ({ fail: 0, label, pass: 0, passRate: null, unknown: 0 }),
    ),
    status: "unavailable",
    summary: {
      codeQuality: unavailableMetric,
      efficiency: unavailableMetric,
      reviewCoverage: unavailableMetric,
      skillAdherence: unavailableMetric,
      standardsCompliance: unavailableMetric,
      taskCompletion: unavailableMetric,
    },
    trends: [],
    usage: { costUsd: null, outputTokens: 0, totalTokens: 0 },
    window: { label: "Last 30 days", since: "", until: "" },
  };
}

export function createUnavailableAiEngineeringEvaluations(): AiEngineeringEvaluations {
  const unavailableMetric: AiEngineeringMetric = {
    denominator: 0,
    numerator: 0,
    state: "unavailable",
    value: null,
  };
  return {
    availability: "unavailable",
    calibrated: false,
    completedRuns: 0,
    coverage: unavailableMetric,
    eligibleRuns: 0,
    failedRuns: 0,
    judgeModels: [],
    labels: { fail: 0, partial: 0, pass: 0, unknown: 0 },
    passRate: unavailableMetric,
    rubricVersions: [],
    sampledRuns: 0,
    scoredRuns: 0,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isMetric(value: unknown): value is AiEngineeringMetric {
  if (!isRecord(value)) return false;
  return (
    typeof value.denominator === "number" &&
    typeof value.numerator === "number" &&
    (value.state === "measured" ||
      value.state === "insufficient_data" ||
      value.state === "unavailable") &&
    (typeof value.value === "number" || value.value === null)
  );
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isNullableNumber(value: unknown): value is number | null {
  return value === null || isFiniteNumber(value);
}

function isOutcomeCohort(value: unknown): value is AiEngineeringOutcomeCohort {
  return (
    isRecord(value) &&
    (value.cohort === "ai" || value.cohort === "other" || value.cohort === "all") &&
    isMetric(value.changesRequestedShare) &&
    isNullableNumber(value.medianHoursToMerge) &&
    isNullableNumber(value.medianHumanComments) &&
    isNullableNumber(value.medianHumanReviews) &&
    isNullableNumber(value.medianLinesChanged) &&
    isFiniteNumber(value.mergedPrs) &&
    isMetric(value.revertRate) &&
    isMetric(value.reworkShare)
  );
}

function isReviewWorkflow(value: unknown): value is AiEngineeringReviewWorkflow {
  return (
    isRecord(value) &&
    (value.availability === "live" ||
      value.availability === "partial" ||
      value.availability === "unavailable") &&
    isMetric(value.findingCoverage) &&
    isMetric(value.findingsActedOn) &&
    isMetric(value.reviewCoverage) &&
    Array.isArray(value.dimensions) &&
    value.dimensions.every(
      (row) =>
        isRecord(row) &&
        typeof row.dimension === "string" &&
        isFiniteNumber(row.findings) &&
        (row.state === "measured" ||
          row.state === "insufficient_data" ||
          row.state === "unavailable"),
    )
  );
}

function isOutcomes(value: unknown): value is AiEngineeringOutcomes {
  if (!isRecord(value) || !isRecord(value.aiReview)) return false;
  return (
    isMetric(value.adoption) &&
    isMetric(value.aiReview.changedAfterFinding) &&
    isMetric(value.aiReview.changesRequestedOnOtherPrs) &&
    isMetric(value.aiReview.changesRequestedOnReviewedPrs) &&
    isNullableNumber(value.aiReview.medianHumanCommentsOnOtherPrs) &&
    isNullableNumber(value.aiReview.medianHumanCommentsOnReviewedPrs) &&
    isFiniteNumber(value.aiReview.mergedPrsReviewed) &&
    isFiniteNumber(value.aiReview.mergedPrsWithFindings) &&
    (value.aiReview.workflow === undefined || isReviewWorkflow(value.aiReview.workflow)) &&
    (value.availability === "live" || value.availability === "unavailable") &&
    Array.isArray(value.cohorts) &&
    value.cohorts.every(isOutcomeCohort) &&
    isNullableNumber(value.costPerAiMergedPr) &&
    isNullableNumber(value.costPerDeployedTicket) &&
    isNullableNumber(value.deployedTickets) &&
    isFiniteNumber(value.mergedPrs) &&
    isFiniteNumber(value.mergedPrsWithAi) &&
    isFiniteNumber(value.revertedPrs) &&
    isMetric(value.sessionConversion)
  );
}

function isEvaluations(value: unknown): value is AiEngineeringEvaluations {
  if (!isRecord(value) || !isRecord(value.labels)) return false;
  return (
    (value.availability === "live" || value.availability === "unavailable") &&
    typeof value.calibrated === "boolean" &&
    isFiniteNumber(value.completedRuns) &&
    isMetric(value.coverage) &&
    isFiniteNumber(value.eligibleRuns) &&
    isFiniteNumber(value.failedRuns) &&
    Array.isArray(value.judgeModels) &&
    value.judgeModels.every((model) => typeof model === "string") &&
    isFiniteNumber(value.labels.fail) &&
    isFiniteNumber(value.labels.partial) &&
    isFiniteNumber(value.labels.pass) &&
    isFiniteNumber(value.labels.unknown) &&
    isMetric(value.passRate) &&
    Array.isArray(value.rubricVersions) &&
    value.rubricVersions.every((version) => typeof version === "string") &&
    isFiniteNumber(value.sampledRuns) &&
    isFiniteNumber(value.scoredRuns)
  );
}

function isMetricState(value: unknown): value is AiEngineeringMetricState {
  return value === "measured" || value === "insufficient_data" || value === "unavailable";
}

export function isAiEngineeringSnapshotResponse(
  value: unknown,
): value is AiEngineeringSnapshotResponse {
  if (
    !isRecord(value) ||
    !isRecord(value.summary) ||
    !isRecord(value.review) ||
    !isRecord(value.provenance)
  )
    return false;
  if (!isRecord(value.freshness) || !isRecord(value.usage) || !isRecord(value.window)) {
    return false;
  }
  return (
    (value.status === "live" || value.status === "partial" || value.status === "unavailable") &&
    isMetric(value.summary.codeQuality) &&
    isMetric(value.summary.efficiency) &&
    isMetric(value.summary.reviewCoverage) &&
    isMetric(value.summary.skillAdherence) &&
    isMetric(value.summary.standardsCompliance) &&
    isMetric(value.summary.taskCompletion) &&
    isEvaluations(value.evaluations) &&
    isOutcomes(value.outcomes) &&
    Array.isArray(value.breakouts) &&
    value.breakouts.every(
      (row) =>
        isRecord(row) &&
        typeof row.cohort === "string" &&
        (row.kind === "all" || row.kind === "engineer" || row.kind === "repo") &&
        isFiniteNumber(row.aiComments) &&
        (row.authoredPrsOpened === null || isFiniteNumber(row.authoredPrsOpened)) &&
        isFiniteNumber(row.prCount) &&
        isFiniteNumber(row.sessionCount) &&
        isFiniteNumber(row.skillInvocations) &&
        isFiniteNumber(row.turnCount) &&
        (row.standardsPassRate === null || isFiniteNumber(row.standardsPassRate)),
    ) &&
    Array.isArray(value.review.dimensions) &&
    value.review.dimensions.every(
      (row) =>
        isRecord(row) &&
        typeof row.dimension === "string" &&
        isFiniteNumber(row.findings) &&
        isMetricState(row.state),
    ) &&
    Array.isArray(value.skills) &&
    value.skills.every(
      (row) =>
        isRecord(row) &&
        typeof row.skill === "string" &&
        isFiniteNumber(row.completed) &&
        isFiniteNumber(row.invocations) &&
        (row.adherenceRate === null || isFiniteNumber(row.adherenceRate)),
    ) &&
    Array.isArray(value.standards) &&
    value.standards.every(
      (row) =>
        isRecord(row) &&
        typeof row.label === "string" &&
        isFiniteNumber(row.pass) &&
        isFiniteNumber(row.fail) &&
        isFiniteNumber(row.unknown) &&
        (row.passRate === null || isFiniteNumber(row.passRate)),
    ) &&
    Array.isArray(value.trends) &&
    value.trends.every(
      (row) =>
        isRecord(row) &&
        typeof row.date === "string" &&
        isMetric(row.adoption) &&
        isMetric(row.costPerAiMergedPr) &&
        isMetric(row.findingsActedOn) &&
        isMetric(row.sessionConversion) &&
        isMetric(row.efficiency) &&
        isMetric(row.reviewCoverage) &&
        isFiniteNumber(row.reviewRequests) &&
        isMetric(row.standardsCompliance) &&
        isMetric(row.taskCompletion),
    ) &&
    Array.isArray(value.provenance.models) &&
    value.provenance.models.every((model) => typeof model === "string") &&
    isFiniteNumber(value.provenance.prCount) &&
    isFiniteNumber(value.provenance.sessionCount) &&
    (value.freshness.github === "live" ||
      value.freshness.github === "partial" ||
      value.freshness.github === "unavailable") &&
    (value.freshness.operators === "live" || value.freshness.operators === "unavailable") &&
    isFiniteNumber(value.review.aiComments) &&
    isFiniteNumber(value.review.completedTurns) &&
    isFiniteNumber(value.review.humanComments) &&
    isFiniteNumber(value.review.prCount) &&
    isFiniteNumber(value.review.reviewRequests) &&
    isFiniteNumber(value.review.reviewSessions) &&
    isFiniteNumber(value.review.successfulTurns) &&
    isFiniteNumber(value.usage.outputTokens) &&
    isFiniteNumber(value.usage.totalTokens) &&
    (value.usage.costUsd === null || isFiniteNumber(value.usage.costUsd)) &&
    typeof value.generatedAt === "string" &&
    typeof value.window.label === "string" &&
    typeof value.window.since === "string" &&
    typeof value.window.until === "string"
  );
}

const OUTCOME_TREND_KEYS = [
  "adoption",
  "costPerAiMergedPr",
  "findingsActedOn",
  "sessionConversion",
] as const;

/**
 * Accept a response from a control plane that predates the outcome measures.
 * The web app deploys automatically on merge while the control plane is a
 * manual deploy, so the two can disagree for a while. A payload without
 * `outcomes` (or without the outcome trend fields) is filled in as unavailable
 * and then held to the strict contract; anything else malformed still fails.
 */
export function normalizeAiEngineeringSnapshot(
  value: unknown,
): AiEngineeringSnapshotResponse | null {
  if (!isRecord(value)) return null;
  const unavailable: AiEngineeringMetric = {
    denominator: 0,
    numerator: 0,
    state: "unavailable",
    value: null,
  };
  const candidate: Record<string, unknown> = { ...value };
  if (candidate.outcomes === undefined) {
    candidate.outcomes = createUnavailableAiEngineeringOutcomes();
  }
  if (candidate.evaluations === undefined) {
    candidate.evaluations = createUnavailableAiEngineeringEvaluations();
  }
  if (Array.isArray(candidate.trends)) {
    candidate.trends = candidate.trends.map((row: unknown) => {
      if (!isRecord(row)) return row;
      const filled: Record<string, unknown> = { ...row };
      for (const key of OUTCOME_TREND_KEYS) {
        if (filled[key] === undefined) filled[key] = unavailable;
      }
      return filled;
    });
  }
  return isAiEngineeringSnapshotResponse(candidate) ? candidate : null;
}
